import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Divider,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
} from '@mui/material';
import {
  Sync,
  SyncProblem,
  SyncDisabled,
  CloudSync,
  CloudUpload,
  CloudDownload,
  CloudOff,
  Cloud,
  Storage,
  Smartphone,
  TabletMac,
  Computer,
  Wifi,
  WifiOff,
  SignalCellular4Bar,
  SignalWifiOff,
  Battery3Bar,
  BatteryAlert,
  Memory,
  SdStorage,
  Folder,
  FolderOpen,
  InsertDriveFile,
  Image,
  Movie,
  Description,
  Settings,
  Refresh,
  PlayArrow,
  Pause,
  Stop,
  CheckCircle,
  Error,
  Warning,
  Info,
  Schedule,
  Today,
  DateRange,
  AccessTime,
  Person,
  Group,
  Work,
  Business,
  LocationOn,
  Search,
  FilterList,
  Visibility,
  Edit,
  Delete,
  Add,
  Download,
  Upload,
  Share,
  History,
  Timeline,
  TrendingUp,
  TrendingDown,
  Speed,
  DataUsage,
  NetworkCheck,
  Router,
  DeviceHub,
  ExpandMore,
} from '@mui/icons-material';
import mobileSyncService from '../services/mobileSyncService';

interface UISyncDevice {
  id: string;
  name: string;
  deviceType: 'smartphone' | 'tablet' | 'laptop';
  userId: string;
  userName: string;
  userRole: string;
  status: 'online' | 'offline' | 'syncing' | 'error' | 'paused';
  lastSync: string;
  nextSync?: string;
  syncProgress: number;
  connectionType: 'wifi' | 'cellular' | 'offline';
  signalStrength: number;
  batteryLevel: number;
  storageUsed: number;
  storageTotal: number;
  syncSettings: {
    autoSync: boolean;
    syncOnWifiOnly: boolean;
    syncFrequency: 'realtime' | 'hourly' | 'daily' | 'manual';
    syncTypes: string[];
  };
  pendingUploads: number;
  pendingDownloads: number;
  conflictCount: number;
  dataUsage: {
    today: number;
    thisWeek: number;
    thisMonth: number;
  };
  location: {
    latitude: number;
    longitude: number;
    address: string;
  };
  workSite?: string;
}

interface UISyncJob {
  id: string;
  deviceId: string;
  deviceName: string;
  userName: string;
  type: 'upload' | 'download' | 'bidirectional';
  status: 'pending' | 'running' | 'completed' | 'failed' | 'paused';
  progress: number;
  startTime: string;
  endTime?: string;
  duration?: number;
  dataType: 'photos' | 'videos' | 'documents' | 'projects' | 'all';
  fileCount: number;
  totalSize: number;
  transferredSize: number;
  transferSpeed: number;
  errorMessage?: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  retryCount: number;
  maxRetries: number;
}

interface SyncConflict {
  _id: string;
  jobId: string;
  fileId: string;
  fileName: string;
  conflictType: 'version' | 'deletion' | 'permission';
  localVersion: {
    modifiedAt: string;
    size: number;
    checksum: string;
  };
  remoteVersion: {
    modifiedAt: string;
    size: number;
    checksum: string;
  };
  resolution?: 'keep-local' | 'keep-remote' | 'merge' | 'skip';
  resolvedAt?: string;
  resolvedBy?: string;
  createdAt: string;
}

interface SyncStats {
  totalDevices: number;
  onlineDevices: number;
  syncingDevices: number;
  totalJobs: number;
  activeJobs: number;
  completedJobs: number;
  failedJobs: number;
  totalDataTransferred: number;
  averageSpeed: number;
  conflictsResolved: number;
  pendingConflicts: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`mobile-sync-tabpanel-${index}`}
      aria-labelledby={`mobile-sync-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}





const MobileSync: React.FC = () => {
  const [devices, setDevices] = useState<UISyncDevice[]>([]);
  const [syncJobs, setSyncJobs] = useState<UISyncJob[]>([]);
  const [conflicts, setConflicts] = useState<SyncConflict[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deviceTypeFilter, setDeviceTypeFilter] = useState<string>('all');
  const [selectedDevice, setSelectedDevice] = useState<UISyncDevice | null>(null);
  const [selectedJob, setSelectedJob] = useState<UISyncJob | null>(null);
  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
  const [jobDialogOpen, setJobDialogOpen] = useState(false);

  // Transform service data to UI format
  const transformDevice = (device: any): UISyncDevice => ({
    id: device._id,
    name: device.name,
    deviceType: device.deviceType,
    userId: device.userId,
    userName: device.userName || 'Unknown User',
    userRole: 'Field Worker', // Default role
    status: device.status === 'error' ? 'error' : device.status,
    lastSync: device.lastSync,
    nextSync: undefined,
    syncProgress: device.status === 'syncing' ? 75 : 0,
    connectionType: device.connectionType,
    signalStrength: device.signalStrength || 100,
    batteryLevel: device.batteryLevel || 100,
    storageUsed: device.storageUsed,
    storageTotal: device.storageTotal,
    syncSettings: {
      autoSync: device.syncSettings.autoSync,
      syncOnWifiOnly: device.syncSettings.syncOnWifiOnly,
      syncFrequency: 'daily',
      syncTypes: ['photos', 'documents', 'projects']
    },
    pendingUploads: 0,
    pendingDownloads: 0,
    conflictCount: 0,
    dataUsage: {
      today: Math.floor(device.storageUsed * 0.1),
      thisWeek: Math.floor(device.storageUsed * 0.3),
      thisMonth: Math.floor(device.storageUsed * 0.7)
    },
    location: device.location || { latitude: 0, longitude: 0, address: 'Unknown' },
    workSite: undefined
  });

  const transformJob = (job: any): UISyncJob => ({
    id: job._id,
    deviceId: job.deviceId,
    deviceName: job.deviceName,
    userName: 'Unknown User',
    type: job.type,
    status: job.status === 'cancelled' ? 'failed' : job.status,
    progress: job.processedFiles && job.totalFiles ? (job.processedFiles / job.totalFiles) * 100 : 0,
    startTime: job.startedAt || job.createdAt,
    endTime: job.completedAt,
    duration: job.completedAt && job.startedAt ? 
      new Date(job.completedAt).getTime() - new Date(job.startedAt).getTime() : undefined,
    dataType: job.dataType,
    fileCount: job.totalFiles,
    totalSize: job.totalSize,
    transferredSize: job.processedSize,
    transferSpeed: job.speed || 0,
    errorMessage: job.error,
    priority: 'medium',
    retryCount: 0,
    maxRetries: 3
  });
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [devicesData, jobsData, conflictsData] = await Promise.all([
          mobileSyncService.getDevices(),
          mobileSyncService.getSyncJobs(),
          mobileSyncService.getConflicts()
        ]);
        setDevices(devicesData.map(transformDevice));
        setSyncJobs(jobsData.map(transformJob));
        setConflicts(conflictsData);
      } catch (error) {
        console.error('Error fetching mobile sync data:', error);
        // Set empty arrays as fallback
        setDevices([]);
        setSyncJobs([]);
        setConflicts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Ensure arrays are always arrays for safe operations
  const devicesArray = Array.isArray(devices) ? devices : [];
  const syncJobsArray = Array.isArray(syncJobs) ? syncJobs : [];
  const conflictsArray = Array.isArray(conflicts) ? conflicts : [];

  const filteredDevices = devicesArray.filter(device => {
    const matchesSearch = device.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         device.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         device.location.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || device.status === statusFilter;
    const matchesType = deviceTypeFilter === 'all' || device.deviceType === deviceTypeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });
  
  const stats: SyncStats = {
    totalDevices: devicesArray.length,
    onlineDevices: devicesArray.filter(d => d.status === 'online' || d.status === 'syncing').length,
    syncingDevices: devicesArray.filter(d => d.status === 'syncing').length,
    totalJobs: syncJobsArray.length,
    activeJobs: syncJobsArray.filter(j => j.status === 'running' || j.status === 'pending').length,
    completedJobs: syncJobsArray.filter(j => j.status === 'completed').length,
    failedJobs: syncJobsArray.filter(j => j.status === 'failed').length,
    totalDataTransferred: syncJobsArray.reduce((sum, j) => sum + (j?.transferredSize || 0), 0),
    averageSpeed: (() => {
      const jobsWithSpeed = syncJobsArray.filter(j => j.transferSpeed > 0);
      return jobsWithSpeed.length > 0 
        ? jobsWithSpeed.reduce((sum, j) => sum + (j?.transferSpeed || 0), 0) / jobsWithSpeed.length 
        : 0;
    })(),
    conflictsResolved: conflictsArray.filter(c => c.resolution !== undefined).length,
    pendingConflicts: conflictsArray.filter(c => c.resolution === undefined).length,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'success';
      case 'syncing': return 'info';
      case 'paused': return 'warning';
      case 'offline': return 'default';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getJobStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'running': return 'info';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      case 'paused': return 'default';
      default: return 'default';
    }
  };

  const getDeviceTypeIcon = (type: string) => {
    switch (type) {
      case 'smartphone': return <Smartphone />;
      case 'tablet': return <TabletMac />;
      case 'laptop': return <Computer />;
      default: return <DeviceHub />;
    }
  };

  const getConnectionIcon = (type: string) => {
    switch (type) {
      case 'wifi': return <Wifi color="success" />;
      case 'cellular': return <SignalCellular4Bar color="warning" />;
      case 'offline': return <WifiOff color="error" />;
      default: return <WifiOff />;
    }
  };

  const getSyncTypeIcon = (type: string) => {
    switch (type) {
      case 'upload': return <CloudUpload color="info" />;
      case 'download': return <CloudDownload color="success" />;
      case 'bidirectional': return <CloudSync color="primary" />;
      default: return <Sync />;
    }
  };

  const formatFileSize = (sizeInMB: number) => {
    if (sizeInMB >= 1024) {
      return `${(sizeInMB / 1024).toFixed(1)} GB`;
    }
    return `${sizeInMB.toFixed(1)} MB`;
  };

  const formatSpeed = (speedInMBps: number) => {
    return `${speedInMBps.toFixed(1)} MB/s`;
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleViewDevice = (device: UISyncDevice) => {
    setSelectedDevice(device);
    setDeviceDialogOpen(true);
  };

  const handleViewJob = (job: UISyncJob) => {
    setSelectedJob(job);
    setJobDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Mobile Sync
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
          >
            Sync All
          </Button>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            onClick={() => setSettingsDialogOpen(true)}
          >
            Settings
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ bgcolor: 'primary.main' }}
          >
            Add Device
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DeviceHub color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Total Devices
                </Typography>
                <Typography variant="h5">
                  {stats.totalDevices}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {stats.onlineDevices} online
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CloudSync color="info" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Active Syncs
                </Typography>
                <Typography variant="h5">
                  {stats.syncingDevices}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {stats.activeJobs} jobs running
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DataUsage color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Data Transferred
                </Typography>
                <Typography variant="h5">
                  {formatFileSize(stats.totalDataTransferred)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Avg: {formatSpeed(stats.averageSpeed)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Warning color="warning" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Conflicts
                </Typography>
                <Typography variant="h5">
                  {stats.pendingConflicts}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {stats.conflictsResolved} resolved
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Devices" icon={<DeviceHub />} />
          <Tab label="Sync Jobs" icon={<CloudSync />} />
          <Tab label="Conflicts" icon={<Warning />} />
          <Tab label="Analytics" icon={<TrendingUp />} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search devices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ minWidth: 250 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="syncing">Syncing</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Device Type</InputLabel>
              <Select
                value={deviceTypeFilter}
                label="Device Type"
                onChange={(e) => setDeviceTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="smartphone">Smartphone</MenuItem>
                <MenuItem value="tablet">Tablet</MenuItem>
                <MenuItem value="laptop">Laptop</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setDeviceTypeFilter('all');
              }}
            >
              Refresh
            </Button>
          </Box>
        </Paper>

        {/* Devices Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Device</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Connection</TableCell>
                <TableCell>Sync Progress</TableCell>
                <TableCell>Pending</TableCell>
                <TableCell>Data Usage</TableCell>
                <TableCell>Last Sync</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDevices.map((device) => (
                <TableRow key={device.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {getDeviceTypeIcon(device.deviceType)}
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {device.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {device.deviceType.toUpperCase()}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {device.userName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {device.userRole}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={device.status}
                        color={getStatusColor(device.status) as any}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                      {device.conflictCount > 0 && (
                        <Badge badgeContent={device.conflictCount} color="error">
                          <Warning color="warning" />
                        </Badge>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getConnectionIcon(device.connectionType)}
                      <Box>
                        <Typography variant="body2">
                          {device.connectionType}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Battery: {device.batteryLevel}%
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {device.status === 'syncing' ? (
                      <Box>
                        <LinearProgress
                          variant="determinate"
                          value={device.syncProgress}
                          sx={{ mb: 0.5, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {device.syncProgress}% complete
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {device.status === 'online' ? 'Ready' : 'Not syncing'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        ↑{device.pendingUploads} ↓{device.pendingDownloads}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Upload/Download
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {formatFileSize(device.dataUsage.today)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Today
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(device.lastSync)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDevice(device)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sync Now">
                        <IconButton size="small">
                          <Sync />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Settings">
                        <IconButton size="small">
                          <Settings />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Job</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Speed</TableCell>
                <TableCell>Files</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {syncJobs.map((job) => (
                <TableRow key={job.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {job.dataType.charAt(0).toUpperCase() + job.dataType.slice(1)} Sync
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Priority: {job.priority}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {job.deviceName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {job.userName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getSyncTypeIcon(job.type)}
                      <Typography variant="body2">
                        {job.type}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={job.status}
                      color={getJobStatusColor(job.status) as any}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    {job.status === 'running' ? (
                      <Box>
                        <LinearProgress
                          variant="determinate"
                          value={job.progress}
                          sx={{ mb: 0.5, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(job.transferredSize)} / {formatFileSize(job.totalSize)}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2">
                        {job.progress}%
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {job.transferSpeed > 0 ? formatSpeed(job.transferSpeed) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {job.fileCount} files
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {job.duration ? formatDuration(job.duration) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewJob(job)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {job.status === 'running' && (
                        <Tooltip title="Pause">
                          <IconButton size="small">
                            <Pause />
                          </IconButton>
                        </Tooltip>
                      )}
                      {job.status === 'failed' && (
                        <Tooltip title="Retry">
                          <IconButton size="small">
                            <Refresh />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {conflicts.length === 0 ? (
          <Alert severity="success">
            No sync conflicts found. All devices are synchronized properly.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {conflicts.map((conflict) => (
              <Card key={conflict._id}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      {conflict.fileName}
                    </Typography>
                    <Chip
                      label={conflict.conflictType}
                      color="warning"
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Job ID: {conflict.jobId} • Created: {formatDate(conflict.createdAt)}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Local Version
                      </Typography>
                      <Typography variant="body2">
                        Modified: {formatDate(conflict.localVersion.modifiedAt)}
                      </Typography>
                      <Typography variant="body2">
                        Size: {formatFileSize(conflict.localVersion.size)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Remote Version
                      </Typography>
                      <Typography variant="body2">
                        Modified: {formatDate(conflict.remoteVersion.modifiedAt)}
                      </Typography>
                      <Typography variant="body2">
                        Size: {formatFileSize(conflict.remoteVersion.size)}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                    <Button size="small" variant="outlined" color="primary">
                      Keep Local
                    </Button>
                    <Button size="small" variant="outlined" color="secondary">
                      Keep Remote
                    </Button>
                    <Button size="small" variant="outlined">
                      Merge
                    </Button>
                    <Button size="small" variant="outlined" color="error">
                      Skip
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
          <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sync Performance
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Success Rate
                </Typography>
                <Typography variant="h4" color="success.main">
                  {((stats.completedJobs / stats.totalJobs) * 100).toFixed(1)}%
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Average Speed
                </Typography>
                <Typography variant="h4" color="primary">
                  {formatSpeed(stats.averageSpeed)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
          
          <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Data Usage Breakdown
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CloudUpload color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Uploads"
                    secondary={formatFileSize(devicesArray.reduce((sum, d) => sum + (d?.dataUsage?.today || 0) * 0.6, 0))}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CloudDownload color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Downloads"
                    secondary={formatFileSize(devicesArray.reduce((sum, d) => sum + (d?.dataUsage?.today || 0) * 0.4, 0))}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
          
          <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Device Health
              </Typography>
              <List dense>
                {['online', 'syncing', 'paused', 'offline', 'error'].map((status) => {
                  const count = devices.filter(d => d.status === status).length;
                  const percentage = (count / stats.totalDevices * 100).toFixed(1);
                  return (
                    <ListItem key={status}>
                      <ListItemIcon>
                        <Chip
                          label=""
                          color={getStatusColor(status) as any}
                          size="small"
                          sx={{ width: 16, height: 16, minWidth: 16 }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={status.charAt(0).toUpperCase() + status.slice(1)}
                        secondary={`${count} devices (${percentage}%)`}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      {/* Device Details Dialog */}
      <Dialog
        open={deviceDialogOpen}
        onClose={() => setDeviceDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Device Details - {selectedDevice?.name}
        </DialogTitle>
        <DialogContent>
          {selectedDevice && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                <Card sx={{ flex: 1, minWidth: 300 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Device Information
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>User:</strong> {selectedDevice.userName} ({selectedDevice.userRole})
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Location:</strong> {selectedDevice.location.address}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Work Site:</strong> {selectedDevice.workSite}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Connection:</strong> {selectedDevice.connectionType}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Battery:</strong> {selectedDevice.batteryLevel}%
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card sx={{ flex: 1, minWidth: 300 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Sync Settings
                    </Typography>
                    <FormControlLabel
                      control={<Switch checked={selectedDevice.syncSettings.autoSync} />}
                      label="Auto Sync"
                    />
                    <FormControlLabel
                      control={<Switch checked={selectedDevice.syncSettings.syncOnWifiOnly} />}
                      label="WiFi Only"
                    />
                    <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
                      <strong>Frequency:</strong> {selectedDevice.syncSettings.syncFrequency}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Sync Types:</strong> {selectedDevice.syncSettings.syncTypes.join(', ')}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Data Usage Statistics
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Today
                      </Typography>
                      <Typography variant="h6">
                        {formatFileSize(selectedDevice.dataUsage.today)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        This Week
                      </Typography>
                      <Typography variant="h6">
                        {formatFileSize(selectedDevice.dataUsage.thisWeek)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        This Month
                      </Typography>
                      <Typography variant="h6">
                        {formatFileSize(selectedDevice.dataUsage.thisMonth)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeviceDialogOpen(false)}>Close</Button>
          <Button variant="outlined" startIcon={<Settings />}>
            Configure
          </Button>
          <Button variant="contained" startIcon={<Sync />}>
            Sync Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Job Details Dialog */}
      <Dialog
        open={jobDialogOpen}
        onClose={() => setJobDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Sync Job Details
        </DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedJob.dataType.charAt(0).toUpperCase() + selectedJob.dataType.slice(1)} Sync
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Device:</strong> {selectedJob.deviceName}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>User:</strong> {selectedJob.userName}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Type:</strong> {selectedJob.type}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Priority:</strong> {selectedJob.priority}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Files:</strong> {selectedJob.fileCount}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Size:</strong> {formatFileSize(selectedJob.totalSize)}
                </Typography>
              </Box>
              
              {selectedJob.status === 'running' && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Progress
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={selectedJob.progress}
                    sx={{ mb: 1, height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {formatFileSize(selectedJob.transferredSize)} / {formatFileSize(selectedJob.totalSize)} ({selectedJob.progress}%)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Speed: {formatSpeed(selectedJob.transferSpeed)}
                  </Typography>
                </Box>
              )}
              
              {selectedJob.errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {selectedJob.errorMessage}
                </Alert>
              )}
              
              <Typography variant="body2" color="text.secondary">
                Started: {formatDate(selectedJob.startTime)}
              </Typography>
              {selectedJob.endTime && (
                <Typography variant="body2" color="text.secondary">
                  Ended: {formatDate(selectedJob.endTime)}
                </Typography>
              )}
              {selectedJob.duration && (
                <Typography variant="body2" color="text.secondary">
                  Duration: {formatDuration(selectedJob.duration)}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJobDialogOpen(false)}>Close</Button>
          {selectedJob?.status === 'running' && (
            <Button variant="outlined" startIcon={<Pause />}>
              Pause
            </Button>
          )}
          {selectedJob?.status === 'failed' && (
            <Button variant="contained" startIcon={<Refresh />}>
              Retry
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Mobile Sync Settings
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6">Global Settings</Typography>
            
            <FormControl fullWidth>
              <InputLabel>Default Sync Frequency</InputLabel>
              <Select label="Default Sync Frequency" defaultValue="hourly">
                <MenuItem value="realtime">Real-time</MenuItem>
                <MenuItem value="hourly">Hourly</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="manual">Manual</MenuItem>
              </Select>
            </FormControl>
            
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Network Settings
              </Typography>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Allow Cellular Sync"
              />
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Compress Data"
              />
              <FormControlLabel
                control={<Switch />}
                label="Pause on Low Battery"
              />
            </Box>
            
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Conflict Resolution
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Default Resolution</InputLabel>
                <Select label="Default Resolution" defaultValue="manual">
                  <MenuItem value="use-local">Always Use Local</MenuItem>
                  <MenuItem value="use-server">Always Use Server</MenuItem>
                  <MenuItem value="use-newer">Use Newer Version</MenuItem>
                  <MenuItem value="manual">Manual Resolution</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Save Settings</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MobileSync;